import { useEffect, useState, useTransition } from 'react';
import { NEXT_REST_ENTITY, RestEntityQueryResponseMap } from '@common/interfaces';
import { getCachedToken } from '@config/authentication';
import { nextRestApiCallWithToken } from '@utils/serverActions/clientUtility';

export interface IUseRestCallOptions<MAPPED_ENTITY extends NEXT_REST_ENTITY> {
  body?: Record<string, any>;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  nextOptions?: Record<string, any>;
  skip?: boolean;
  orderId?: string;
  onCompleted?: (data: RestEntityQueryResponseMap[MAPPED_ENTITY]) => void;
  onError?: (error: any) => void;
  defaultLoading?: boolean;
}

export interface IUseRestCallResult<MAPPED_ENTITY extends NEXT_REST_ENTITY> {
  data: RestEntityQueryResponseMap[MAPPED_ENTITY] | null;
  loading: boolean;
  error: any;
}

export function useRestCall<MAPPED_ENTITY extends NEXT_REST_ENTITY>(
  entity: MAPPED_ENTITY,
  options: IUseRestCallOptions<MAPPED_ENTITY> = {}
): IUseRestCallResult<MAPPED_ENTITY> {
  const {
    body = {},
    nextOptions = { cache: 'no-store' },
    skip = false,
    onCompleted,
    onError,
    orderId,
    method = 'GET',
    defaultLoading = false,
  } = options;

  const [loading, setLoading] = useState<boolean>(defaultLoading);
  const [isPending, startTransition] = useTransition();
  const [data, setData] = useState<RestEntityQueryResponseMap[NEXT_REST_ENTITY] | null>(null);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    if (skip) {
      setLoading(false);
      return;
    }

    startTransition(() => {
      (async () => {
        try {
          setLoading(true);
          const result = await nextRestApiCallWithToken(entity, getCachedToken(), method, body, nextOptions, orderId);
          setData(result);
          setError(null);
          if (onCompleted) {
            onCompleted(result);
          }
        } catch (err) {
          setError(err);
          if (onError) {
            onError(err);
          }
        } finally {
          setLoading(false);
        }
      })();
    });
  }, [entity, JSON.stringify(body), skip]);

  return {
    data,
    loading: loading || isPending,
    error,
  };
}
